import { useState, useEffect, useRef } from 'react'
import Modal from './Modal.jsx'
import TermsModal from './TermsModal.jsx'
import PassBar from './PassBar.jsx'
import Enquiry from './Enquiry.jsx'
import PillToggle from './PillToggle.jsx'

const FILTERS = [
  { key: 'all',          label: 'All' },
  { key: 'flexibility',  label: 'Flexibility' },
  { key: 'conditioning', label: 'Conditioning' },
  { key: 'aerial-1to1', label: 'Aerial 1:1' },
  { key: 'aerial-2to1', label: 'Aerial 2:1' },
  { key: 'online',       label: 'Online' },
]

function groupByDate(sessions) {
  const groups = {}
  sessions.forEach(s => {
    if (!groups[s.date]) groups[s.date] = []
    groups[s.date].push(s)
  })
  return Object.entries(groups)
}

function parseDateRail(dateStr) {
  // Expects "Wed, 5 Aug 2026"
  const match = dateStr.match(/^(\w+),\s*(\d+)\s+(\w+)\s+(\d+)$/)
  if (!match) return { day: '', sub: dateStr }
  const [, weekday, day, month] = match
  return { day, sub: `${weekday} ${month}` }
}

function DateRail({ day, sub }) {
  const [count, setCount] = useState(0)
  const [triggered, setTriggered] = useState(false)
  const ref = useRef(null)
  const target = parseInt(day, 10) || 0

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !triggered) {
        setTriggered(true)
        observer.disconnect()
      }
    }, { threshold: 0.4 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [triggered])

  useEffect(() => {
    if (!triggered) return
    let start = null
    const duration = 500
    const step = (ts) => {
      if (!start) start = ts
      const progress = Math.min((ts - start) / duration, 1)
      setCount(Math.round(progress * target))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [triggered, target])

  return (
    <div ref={ref} className="session-day-rail" style={{ paddingTop: 4 }}>
      <div style={{ fontFamily: 'var(--f-d)', fontSize: 36, letterSpacing: '0.02em', color: 'var(--pitch)', lineHeight: 0.9 }}>
        {count}
      </div>
      <div style={{ fontFamily: 'var(--f-m)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.5)', marginTop: 6, fontWeight: 700 }}>
        {sub}
      </div>
    </div>
  )
}

function SessionRow({ session: s }) {
  const handleGlow = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    e.currentTarget.style.setProperty('--glow-x', `${e.clientX - rect.left}px`)
    e.currentTarget.style.setProperty('--glow-y', `${e.clientY - rect.top}px`)
  }
  const [showModal, setShowModal] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [hoveredBook, setHoveredBook] = useState(false)
  const [hoveredPass, setHoveredPass] = useState(false)
  const hasPass = !s.isFull && s.applicablePasses?.length > 0

  return (
    <>
      <div className="glow-card" onMouseMove={handleGlow} style={{ background: 'var(--pitch)', marginBottom: 8, borderRadius: 3 }}>

        {/* Single row: all content inline */}
        <div className="session-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, padding: '16px 20px', alignItems: 'center' }}>

          {/* Left: title + meta stacked */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ fontFamily: 'var(--f-d)', fontSize: 'clamp(14px,2vw,18px)', letterSpacing: '0.06em', color: 'var(--bone)' }}>
                {s.title}
              </div>
              {s.description && (
                <button onClick={() => setShowModal(true)}
                  style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--f-m)', fontSize: 9, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>
                  i
                </button>
              )}
            </div>
            <div style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.8)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span>{s.time}</span>
              {s.durationMins && <span>{s.durationMins} min</span>}
              {s.locationLine && <span>{s.locationLine}</span>}
              {!s.isFull && s.spacesLeft !== undefined && <span>{s.spacesLeft} space{s.spacesLeft !== 1 ? 's' : ''} available</span>}
            </div>
          </div>

          {/* Right: price + buttons */}
          <div className="session-right-col" style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <div style={{ fontFamily: 'var(--f-d)', fontSize: 20, letterSpacing: '0.04em', color: 'var(--bone)' }}>
              {s.price}
            </div>
            {s.isFull ? (
              <span style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.3)' }}>Full</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <a href={s.bookingUrl} target="_blank" rel="noopener noreferrer"
                  onMouseEnter={() => setHoveredBook(true)}
                  onMouseLeave={() => setHoveredBook(false)}
                  style={{ fontFamily: 'var(--f-b)', fontSize: 13, fontWeight: 500, textDecoration: 'none', padding: '8px 16px', borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)', background: hoveredBook ? 'var(--bone)' : 'transparent', color: hoveredBook ? 'var(--pitch)' : 'var(--bone)', transition: 'background 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.15s ease', transform: hoveredBook ? 'translateY(-1px)' : 'translateY(0)', whiteSpace: 'nowrap' }}>
                  Book →
                </a>
                {hasPass && (
                  <a href={s.bookingUrl} target="_blank" rel="noopener noreferrer"
                    onMouseEnter={() => setHoveredPass(true)}
                    onMouseLeave={() => setHoveredPass(false)}
                    style={{ fontFamily: 'var(--f-b)', fontSize: 12, fontWeight: 500, textDecoration: 'none', padding: '8px 16px', borderRadius: 2, border: `1px solid ${hoveredPass ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.12)'}`, background: hoveredPass ? 'rgba(255,255,255,0.06)' : 'transparent', color: hoveredPass ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)', transition: 'background 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.15s ease', transform: hoveredPass ? 'translateY(-1px)' : 'translateY(0)', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    Book with pass →
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pass buy nudge + T&Cs in one line */}
        {!s.isFull && (
          <div style={{ padding: '0 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            {hasPass ? (
              <div style={{ fontFamily: 'var(--f-b)', fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>
                No pass?{' '}
                <a href={s.applicablePasses[0].buyUrl} target="_blank" rel="noopener noreferrer"
                  className="underline-grow" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                  Buy one →
                </a>
              </div>
            ) : <div />}
            <button onClick={() => setShowTerms(true)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--f-b)', fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.55)', textDecoration: 'underline' }}>
              Terms &amp; conditions
            </button>
          </div>
        )}
      </div>

      {showModal && <Modal session={s} onClose={() => setShowModal(false)} />}
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
    </>
  )
}


export default function Sessions({ sessions, passes, loading, error }) {
  const [filter, setFilter] = useState('all')

  const visible = filter === 'all'
    ? sessions
    : filter === 'online'
      ? sessions.filter(s => s.isOnline)
      : sessions.filter(s => s.filter === filter)

  const grouped = groupByDate(visible)

  return (
    <section id="sessions" style={{
      background: 'var(--bone)',
      padding: 'clamp(80px,12vw,140px) clamp(24px,5vw,56px) clamp(64px,10vw,120px)',
    }}>

      <style>{`
        @media (max-width: 640px) {
          .session-day-row { grid-template-columns: 1fr !important; gap: 10px !important; }
          .session-day-rail { display: flex !important; align-items: baseline !important; gap: 10px !important; }
          .session-main-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
          .session-right-col { justify-content: space-between !important; width: 100%; }
        }
      `}</style>

      {/* SEO copy */}
      <p style={{
        fontSize: 14, fontWeight: 300, color: 'rgba(0,0,0,0.5)',
        lineHeight: 1.8, maxWidth: 640, marginBottom: 56,
      }}>
        Private aerial coaching and conditioning in Manchester. One-to-one, two-to-one, and small group sessions covering straps, silks, rope, flexibility, and aerial conditioning — structured around your goals, your level, and what you actually need to work on.
      </p>

      {/* Header + filters */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        marginBottom: 40, flexWrap: 'wrap', gap: 24,
      }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--f-d)', fontSize: 'clamp(36px,6vw,64px)',
            letterSpacing: '0.04em', lineHeight: 1, marginBottom: 8, fontWeight: 400, color: 'var(--pitch)',
          }}>
            Private Aerial Coaching<br />in Manchester
          </h1>
          <div style={{
            fontFamily: 'var(--f-m)', fontSize: 9, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: 'rgba(0,0,0,0.3)',
          }}>
            Upcoming Sessions
          </div>
        </div>

        <PillToggle
          options={FILTERS.map(f => ({ key: f.key, label: f.label }))}
          active={filter}
          onChange={setFilter}
          onDark={false}
        />
      </div>

      {/* Pass bar */}
      <PassBar passes={passes || []} />

      {/* Loading */}
      {loading && (
        <div style={{ padding: '80px 0', display: 'flex', justifyContent: 'center' }}>
          <style>{`@keyframes at-pulse { 0%,100%{opacity:0.15} 50%{opacity:0.7} } .at-wordmark{animation:at-pulse 2s ease-in-out infinite}`}</style>
          <div className="at-wordmark" style={{
            fontFamily: 'var(--f-d)', fontSize: 'clamp(32px,6vw,56px)',
            letterSpacing: '0.14em', color: 'var(--pitch)',
          }}>A. Thomson</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '40px', background: '#111', color: 'var(--bone)', marginBottom: 2 }}>
          <div style={{ fontFamily: 'var(--f-d)', fontSize: 24, letterSpacing: '0.06em', marginBottom: 8 }}>Couldn't load sessions</div>
          <p style={{ fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>{error}</p>
        </div>
      )}

      {/* Grouped sessions */}
      {!loading && !error && (
        grouped.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {grouped.map(([date, dateSessions]) => {
              const { day, sub } = parseDateRail(date)
              return (
                <div key={date} className="session-day-row" style={{
                  display: 'grid', gridTemplateColumns: '90px 1fr', gap: 20,
                  padding: '22px 0', borderBottom: '1px solid rgba(0,0,0,0.08)',
                  alignItems: 'start',
                }}>
                  {/* Date rail */}
                  <DateRail day={day} sub={sub} />

                  {/* Sessions for this date */}
                  <div>
                    {dateSessions.map((s, i) => (
                      <div key={s.id} style={{ animation: 'fade-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards', animationDelay: `${300 + i * 120}ms`, animationFillMode: 'both' }}>
                        <SessionRow session={s} />
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div style={{ padding: '80px 0', textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--f-d)', fontSize: 28, letterSpacing: '0.06em', color: 'rgba(0,0,0,0.2)', marginBottom: 8 }}>
              No sessions found
            </div>
            <p style={{ fontSize: 13, fontWeight: 300, color: 'rgba(0,0,0,0.3)' }}>
              Try a different filter, or get in touch to arrange a bespoke session.
            </p>
          </div>
        )
      )}

      {/* Enquiry */}
      <div style={{ marginTop: 64 }}>
        <Enquiry />
      </div>
    </section>
  )
}
